/** The launcher's fades and the speech bubble's pop and pose, shared by the shell
 *  and the campaign types' content. */

/** Every fade on the wheel: critically damped (ζ = 1), so opacity never
 *  overshoots. A movement's spring on opacity overshot past 0 or 1, motion
 *  clamped it, and the fade read as a hitch — it stalled, then landed. A fade
 *  rides beside its move as a per-value transition (`withFade`). */
export const FADE_SPRING = {
  type: "spring",
  stiffness: 400,
  damping: 40,
  mass: 1,
} as const;
/** The chip's pop: one small overshoot, settled in about a third of a
 *  second (the squash spring wobbled it for a second, into its exit). */
export const CHIP_POP = {
  type: "spring",
  stiffness: 600,
  damping: 26,
  mass: 1,
} as const;
/** Passed as `onUpdate` to every launcher element that fades: it keeps the
 *  fade on motion's frame loop, not a compositor (WAAPI) animation. Motion 12
 *  cancels a finished WAAPI fade and writes its final value a frame later, so
 *  a leaving element flashed back to full opacity for one frame before it was
 *  removed (the jagged end of the speech bubble's exit). On the frame loop
 *  the fade also stays in step with the element's own move. */
export const FADE_ON_FRAMELOOP = () => {};
/** A move's transition with its opacity on the fade spring. */
export function withFade<T extends object>(move: T) {
  return { ...move, opacity: FADE_SPRING };
}

/** A bubble's small random placement (Sebastian, 2026-10-14: "a bit bigger
 *  with some randomness to position and rotation, not too much"): px across
 *  and down, degrees of tilt. */
export interface SpeechPose {
  x: number;
  y: number;
  rotate: number;
}

const jitter = (max: number) => (Math.random() * 2 - 1) * max;

/** A fresh placement, a touch off-square. */
export function speechPose(): SpeechPose {
  return { x: jitter(6), y: jitter(2), rotate: jitter(6) };
}
